// src/components/Control/Features/VoicePanel.tsx
// «Настройки → Голос и звук»: голосовые команды с сайта и голос через робота
// (нода audio на Pi). Сама запись и отправка — в общем VoiceController (robot/voice.ts),
// его же использует голосовой слой (кнопка микрофона в шапке) и экран «Няня».
//  - распознавание: на роботе (Vosk) или в браузере;
//  - текстовая команда: robot/<id>/audio/cmd {"cmd": "text"};
//  - «говорить через робота»: голос в динамик робота (mode "speak");
//  - «сказать»: синтез речи на роботе {"cmd": "say"}; громкость динамика, тест, «замолчать».
import React, { useState } from 'react';
import { putJson } from '../../../hooks/useZenohSubscription';
import { MicCapture } from '../../../audio/pcm';
import { FeatureProps, timeOf } from '../../../types/features';
import { useEventsState, useRobot, useUiActions, useVoiceState } from '../robot/RobotContext';
import { VOICE_EXAMPLES } from '../robot/voice';
import { useAudioState } from './NannyPanel';

// «Сказать» текст роботом и «говорить через робота» — используется и на экране «Няня»
export const RobotSpeech: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { session, robotId, voice, notify } = useRobot();
  const speaking = useVoiceState(v => v.speaking);
  const level = useVoiceState(v => v.level);
  const [sayText, setSayText] = useState('');
  const say = () => {
    if (!sayText.trim()) return;
    putJson(session, `robot/${robotId}/audio/cmd`, { cmd: 'say', text: sayText });
    setSayText('');
  };
  const toggleSpeak = async () => {
    if (speaking) {
      voice.stopSpeak();
      return;
    }
    try {
      await voice.startSpeak();
    } catch (err) {
      notify(`Нет доступа к микрофону: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  };
  return (
    <div className="op-speech">
      <button type="button" className={`ptt-btn ${speaking ? 'recording' : ''}`} disabled={!session} aria-pressed={speaking}
        style={{ background: speaking ? undefined : 'linear-gradient(45deg, #16a085, #1abc9c)' }} onClick={toggleSpeak}>
        {speaking ? '🔴 Вас слышно из робота — нажмите, чтобы закончить' : '🎙 Говорить через робота'}
      </button>
      {speaking && (
        <div className="row"><div className="level-meter" aria-hidden="true"><div className="fill" style={{ width: `${Math.min(100, level * 400)}%` }} /></div></div>
      )}
      <div className="row">
        <input type="text" value={sayText} placeholder={compact ? 'Текст, который скажет робот' : 'Текст, который скажет робот (синтез речи)'}
          aria-label="Текст, который скажет робот" onChange={e => setSayText(e.target.value)} onKeyDown={e => e.key === 'Enter' && say()} />
        <button type="button" className="btn-sm primary" disabled={!session || !sayText.trim()} onClick={say}>Сказать</button>
      </div>
      {!MicCapture.supported() && <p className="hint">Микрофон браузера работает только на https:// или localhost.</p>}
    </div>
  );
};

const VoicePanel: React.FC<FeatureProps> = ({ zenohSession, robotId }) => {
  const prefix = `robot/${robotId}/audio`;
  const { voice } = useRobot();
  const { toggleVoice } = useUiActions();
  const { state, online } = useAudioState();
  const recognizer = useVoiceState(v => v.recognizer);
  const events = useEventsState(e => e.events.filter(ev => ev.source === 'voice').slice(0, 30));
  const [text, setText] = useState('');
  const [volume, setVolume] = useState<number | null>(null);
  const browserRecAvailable = typeof window !== 'undefined' && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  const robotVolume = volume ?? state?.nanny.volume ?? 80;

  const run = (t: string) => {
    toggleVoice(true);
    voice.sendText(t);
  };

  return (
    <div className="feature-card">
      <h3>
        <span>🎙️ Голосовые команды</span>
        {online ? (
          <span className={`badge ${state!.recognition ? 'ok' : 'warn'}`}>
            {state!.recognition ? 'распознавание на роботе' : 'без Vosk на роботе'}
          </span>
        ) : (
          <span className="badge off">нет данных</span>
        )}
      </h3>
      {zenohSession && !online && <p className="hint">Нода audio на роботе не отвечает.</p>}

      <div className="row" role="radiogroup" aria-label="Где распознавать речь">
        <span>Распознавать:</span>
        <label>
          <input type="radio" name={`rec-${robotId}`} checked={recognizer === 'robot'} onChange={() => voice.setRecognizer('robot')} />
          на роботе
        </label>
        <label title={browserRecAvailable ? '' : 'Этот браузер не умеет распознавать речь'}>
          <input type="radio" name={`rec-${robotId}`} checked={recognizer === 'browser'} disabled={!browserRecAvailable}
            onChange={() => voice.setRecognizer('browser')} />
          в браузере
        </label>
      </div>

      <button type="button" className="ptt-btn" disabled={!zenohSession} onClick={() => { toggleVoice(true); voice.startCommand(); }}>
        🎤 Сказать команду
      </button>
      <p className="hint">Та же кнопка микрофона есть в шапке на любом экране.</p>

      <div className="row">
        <input type="text" value={text} placeholder="или напишите команду" aria-label="Текстовая команда" onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && text.trim()) { run(text); setText(''); } }} />
        <button type="button" className="btn-sm primary" disabled={!zenohSession || !text.trim()} onClick={() => { run(text); setText(''); }}>
          Выполнить
        </button>
      </div>
      <p className="hint">Команды: {VOICE_EXAMPLES.map((e, i) => (
        <React.Fragment key={e}>{i > 0 && ', '}<button type="button" className="btn-icon" style={{ padding: 0, color: '#2980b9' }} onClick={() => run(e)}>{e}</button></React.Fragment>
      ))}. Можно начинать со слова «робот» или «Грач»; дворецкий, который слышит «Грач» сам, без кнопки, — в разделе «Умный дом и Грач».</p>

      <h4>Динамик робота</h4>
      <RobotSpeech />
      <label className="slider-row">
        <span>Громкость</span>
        <input type="range" min={0} max={150} step={5} value={robotVolume} disabled={!online}
          onChange={e => setVolume(Number(e.target.value))}
          onPointerUp={() => { putJson(zenohSession, `${prefix}/cmd`, { cmd: 'volume', value: robotVolume }); setTimeout(() => setVolume(null), 1500); }}
          onKeyUp={() => putJson(zenohSession, `${prefix}/cmd`, { cmd: 'volume', value: robotVolume })} />
        <span className="value">{robotVolume}%</span>
      </label>
      <div className="row">
        <button type="button" className="btn-sm" disabled={!online} onClick={() => putJson(zenohSession, `${prefix}/cmd`, { cmd: 'test' })}>Тест звука</button>
        <button type="button" className="btn-sm" disabled={!online} onClick={() => putJson(zenohSession, `${prefix}/cmd`, { cmd: 'stop_sound' })}>Замолчать</button>
        {online && (
          <>
            <span className={`badge ${state!.speaker ? 'ok' : 'alert'}`}>{state!.speaker ? 'динамик есть' : 'нет плеера'}</span>
            <span className={`badge ${state!.tts ? 'ok' : 'alert'}`}>{state!.tts ? 'синтез речи' : 'нет синтеза речи'}</span>
            {state!.speaking && <span className="badge warn">говорит…</span>}
          </>
        )}
      </div>

      {events.length > 0 && (
        <>
          <h4>Журнал</h4>
          <ul className="event-list">
            {events.map(ev => (
              <li key={ev.id} className={ev.kind === 'voice_command' ? 'ok' : ev.kind === 'error' ? 'alert' : 'warn'}>
                <span className="time">{timeOf(ev.at)}</span>
                <span className="text">{ev.text}{ev.heard && ev.kind === 'voice_command' ? ` («${ev.heard}»)` : ''}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default VoicePanel;
